import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type AuthStatus =
  | 'idle'
  | 'checking'
  | 'authenticated'
  | 'unauthenticated'
  | 'expired';

import type { PermissionCode } from '@/features/auth/permissions';

export interface AuthState {
  status: AuthStatus;
  isInitialized: boolean;
  isLoading: boolean;
  lastCheck?: number;
  error?: string;
  permissions: PermissionCode[];
  permissionsLoaded: boolean;
  apiKey: string | null;
}

const initialState: AuthState = {
  status: 'idle',
  isInitialized: false,
  isLoading: false,
  lastCheck: undefined,
  error: undefined,
  permissions: [],
  permissionsLoaded: false,
  apiKey: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthStatus: (state, action: PayloadAction<AuthStatus>) => {
      state.status = action.payload;
      state.lastCheck = Date.now();
      state.error = undefined;
    },
    setAuthLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.status = 'checking';
      }
    },
    setAuthAuthenticated: (state) => {
      state.status = 'authenticated';
      state.isInitialized = true;
      state.isLoading = false;
      state.lastCheck = Date.now();
      state.error = undefined;
    },
    setAuthUnauthenticated: (state) => {
      state.status = 'unauthenticated';
      state.isInitialized = true;
      state.isLoading = false;
      state.lastCheck = Date.now();
      state.error = undefined;
      state.permissions = [];
      state.permissionsLoaded = false;
      state.apiKey = null;
    },
    setAuthExpired: (state) => {
      state.status = 'expired';
      state.isInitialized = true;
      state.isLoading = false;
      state.lastCheck = Date.now();
      state.error = undefined;
      state.permissions = [];
      state.permissionsLoaded = false;
      state.apiKey = null;
    },
    setAuthChecking: (state) => {
      state.status = 'checking';
      state.isLoading = true;
      state.lastCheck = Date.now();
      state.error = undefined;
    },
    initializeAuth: (state) => {
      state.isInitialized = true;
      if (state.status === 'idle') {
        state.status = 'unauthenticated';
      }
    },
    setAuthError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
      state.status = 'unauthenticated';
    },
    clearAuthError: (state) => {
      state.error = undefined;
    },
    setPermissions: (state, action: PayloadAction<PermissionCode[]>) => {
      state.permissions = action.payload;
      state.permissionsLoaded = true;
    },
    clearPermissions: (state) => {
      state.permissions = [];
      state.permissionsLoaded = false;
    },
    setApiKey: (state, action: PayloadAction<string | null>) => {
      state.apiKey = action.payload;
    },
    clearApiKey: (state) => {
      state.apiKey = null;
    },
  },
});

export const {
  setAuthStatus,
  setAuthLoading,
  setAuthAuthenticated,
  setAuthUnauthenticated,
  setAuthExpired,
  setAuthChecking,
  initializeAuth,
  setAuthError,
  clearAuthError,
  setPermissions,
  clearPermissions,
  setApiKey,
  clearApiKey,
} = authSlice.actions;

// Selectors
export const selectAuthStatus = (state: { auth: AuthState }) =>
  state.auth.status;
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  state.auth.status === 'authenticated';
export const selectIsAuthChecking = (state: { auth: AuthState }) =>
  state.auth.status === 'checking';
export const selectIsAuthExpired = (state: { auth: AuthState }) =>
  state.auth.status === 'expired';
export const selectIsAuthInitialized = (state: { auth: AuthState }) =>
  state.auth.isInitialized;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectAuthLoading = (state: { auth: AuthState }) =>
  state.auth.isLoading;
export const selectNeedsAuthCheck = (state: { auth: AuthState }) => {
  const now = Date.now();
  const lastCheck = state.auth.lastCheck;
  const fiveMinutes = 5 * 60 * 1000;

  return !lastCheck || now - lastCheck > fiveMinutes;
};
export const selectPermissions = (state: { auth: AuthState }) =>
  state.auth.permissions;
export const selectPermissionsLoaded = (state: { auth: AuthState }) =>
  state.auth.permissionsLoaded;
export const selectApiKey = (state: { auth: AuthState }) => state.auth.apiKey;
export default authSlice.reducer;
